import React, { useEffect, useState } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { BeforeAfterSlider } from './BeforeAfterSlider';

export const buildSlides = (item) => {
  const slides = (item.gallery || [item.image]).map((src) => ({ type: 'image', src }));
  const pairs = Array.isArray(item.beforeAfter) ? item.beforeAfter : item.beforeAfter ? [item.beforeAfter] : [];
  return [...pairs.map((pair) => ({ type: 'compare', ...pair })), ...slides];
};

export const Lightbox = ({ slides, title, onClose, initialIndex = 0, testId = 'lightbox' }) => {
  const [index, setIndex] = useState(initialIndex);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') setIndex((i) => (i + 1) % slides.length);
      if (e.key === 'ArrowLeft') setIndex((i) => (i - 1 + slides.length) % slides.length);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = 'auto';
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose, slides.length]);

  const stop = (fn) => (e) => { e.stopPropagation(); fn(); };
  const next = () => setIndex((i) => (i + 1) % slides.length);
  const prev = () => setIndex((i) => (i - 1 + slides.length) % slides.length);
  const slide = slides[index];

  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={onClose} data-testid={testId}>
      <button className="absolute top-6 right-6 text-white hover:text-gray-300 transition-colors z-10" onClick={onClose} aria-label="Close" data-testid={`${testId}-close`}>
        <X className="w-10 h-10" />
      </button>

      {slides.length > 1 && (
        <>
          <button className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 backdrop-blur-sm rounded-full p-3 transition-colors z-10" onClick={stop(prev)} aria-label="Previous image" data-testid={`${testId}-prev`}>
            <ChevronLeft className="w-8 h-8 text-white" />
          </button>
          <button className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 backdrop-blur-sm rounded-full p-3 transition-colors z-10" onClick={stop(next)} aria-label="Next image" data-testid={`${testId}-next`}>
            <ChevronRight className="w-8 h-8 text-white" />
          </button>
        </>
      )}

      <div className="max-w-5xl w-full max-h-[85vh] relative flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
        {slide.type === 'compare' ? (
          <BeforeAfterSlider
            before={slide.before}
            after={slide.after}
            alt={title}
            className="max-w-full"
            style={{ aspectRatio: slide.aspect || '3 / 2', height: `min(75vh, calc(90vw / ${slide.ratio || 1.5}))` }}
            testId={`${testId}-compare`}
          />
        ) : (
          <img src={slide.src} alt={title} className="max-w-full max-h-[75vh] object-contain rounded-lg" data-testid={`${testId}-image`} />
        )}
        <div className="text-center mt-4">
          {title && <p className="text-white text-lg font-medium">{title}</p>}
          {slides.length > 1 && (
            <div className="flex items-center justify-center gap-2 mt-3">
              {slides.map((s, idx) => (
                <button
                  key={`${s.type}-${idx}`}
                  onClick={stop(() => setIndex(idx))}
                  className={`w-2 h-2 rounded-full transition-colors ${idx === index ? 'bg-white' : 'bg-white/40'}`}
                  aria-label={`Slide ${idx + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
